import { createFileRoute, useRouter } from "@tanstack/react-router"
import { useServerFn } from "@tanstack/react-start"
import { AlertTriangleIcon } from "lucide-react"
import { useEffect, useState } from "react"
import { z } from "zod"

import config from "@/config"
import { Alert, AlertDescription, AlertTitle } from "@/ui/alert"
import { Button } from "@/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/dialog"

import { ConfirmDeleteDraft } from "../components/confirm-delete-draft/confirm-delete-draft"
import { InvoiceDetailView } from "../components/invoice-detail/invoice-detail"
import { InvoiceEditor } from "../components/invoice-editor/invoice-editor"
import { PageError, PageLoading } from "../components/page-state/page-state"
import { PaymentDialog } from "../components/payment-dialog/payment-dialog"
import { SendDialog } from "../components/send-dialog/send-dialog"
import {
  createCustomerAction,
  deleteDraftInvoiceAction,
  deletePaymentAction,
  loadCustomers,
  loadInvoice,
  loadInvoiceDocument,
  loadInvoicePdfUrl,
  loadBranding,
  loadInvoiceUsage,
  loadReceiptDocument,
  loadReceiptPdfUrl,
  loadReceipts,
  issueReceiptAction,
  sendReceiptAction,
  updateBrandingAction,
  loadSellerProfile,
  processInvoiceSendAction,
  recordPaymentAction,
  sendInvoiceAction,
  undoInvoiceSendAction,
  updateInvoiceAction,
  updateSellerProfileAction,
  voidInvoiceAction,
} from "../rpc"
import { sydneyToday } from "../status"

const searchSchema = z.object({ send: z.boolean().optional().catch(undefined) })

export const Route = createFileRoute("/_app-shell/invoices/$id")({
  validateSearch: (search) => searchSchema.parse(search),
  loader: async ({ params }) => {
    const invoice = await loadInvoice({ data: { id: params.id } })
    const [seller, customers, usage, document, receipts, brandingView] = await Promise.all([
      loadSellerProfile(),
      loadCustomers(),
      loadInvoiceUsage(),
      invoice.status === "draft"
        ? Promise.resolve(null)
        : loadInvoiceDocument({ data: { id: params.id } }),
      invoice.status === "draft"
        ? Promise.resolve([])
        : loadReceipts({ data: { invoiceId: params.id } }),
      invoice.status === "draft" ? loadBranding().catch(() => null) : Promise.resolve(null),
    ])
    return {
      invoice,
      seller,
      customers,
      usage,
      document,
      receipts,
      branding: brandingView?.branding ?? null,
      today: sydneyToday(new Date()),
    }
  },
  head: () => ({
    meta: [
      { title: `Invoice - ${config.core.projectName}` },
      { name: "robots", content: "noindex" },
    ],
  }),
  pendingComponent: () => <PageLoading title="Invoice" label="Loading the invoice" />,
  errorComponent: ({ error }) => {
    const router = useRouter()
    return (
      <PageError
        title="Invoice"
        message={error.message || "The invoice could not be loaded."}
        onRetry={() => router.invalidate()}
      />
    )
  },
  component: InvoiceDetailRoute,
})

function InvoiceDetailRoute() {
  const { invoice, seller, customers, usage, document, receipts, branding, today } =
    Route.useLoaderData()
  const search = Route.useSearch()
  const router = useRouter()
  // re-fetch after every mutation so the page never shows stale data
  const refresh = () => router.invalidate()

  const updateInvoice = useServerFn(updateInvoiceAction)
  const deleteDraft = useServerFn(deleteDraftInvoiceAction)
  const voidInvoice = useServerFn(voidInvoiceAction)
  const recordPayment = useServerFn(recordPaymentAction)
  const deletePayment = useServerFn(deletePaymentAction)
  const send = useServerFn(sendInvoiceAction)
  const undoSend = useServerFn(undoInvoiceSendAction)
  const processSend = useServerFn(processInvoiceSendAction)
  const createCustomer = useServerFn(createCustomerAction)
  const saveSeller = useServerFn(updateSellerProfileAction)
  const pdfUrl = useServerFn(loadInvoicePdfUrl)
  const issueReceipt = useServerFn(issueReceiptAction)
  const sendReceipt = useServerFn(sendReceiptAction)
  const receiptDocument = useServerFn(loadReceiptDocument)
  const receiptPdf = useServerFn(loadReceiptPdfUrl)
  const saveBranding = useServerFn(updateBrandingAction)

  const [sending, setSending] = useState(Boolean(search.send) && invoice.status === "draft")
  const [payment, setPayment] = useState<"record" | "mark-paid" | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [voiding, setVoiding] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)

  // `?send=true` (from "Save and send") opens the dialog once, then leaves no trace in the URL
  useEffect(() => {
    if (search.send) void router.navigate({ to: ".", search: {}, replace: true })
  }, [search.send, router])

  const isDraft = invoice.status === "draft"
  const notSent = isDraft && invoice.number !== null

  return (
    <>
      {isDraft ? (
        <>
          {notSent && (
            <div className="mx-auto w-full max-w-7xl px-6 pt-8">
              <Alert variant="destructive">
                <AlertTriangleIcon aria-hidden="true" />
                <AlertTitle className="line-clamp-none">
                  Not sent yet. {invoice.number} is kept for the retry.
                </AlertTitle>
                <AlertDescription>
                  <p>Edit the draft if you need to, then send it again with the same number.</p>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" onClick={() => setSending(true)}>
                      Send again
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setVoiding(true)}>
                      Void invoice
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            </div>
          )}
          <InvoiceEditor
            key={invoice.updated_at}
            invoice={invoice}
            seller={seller}
            customers={customers}
            usage={usage}
            today={today}
            onSave={(request) => updateInvoice({ data: { id: invoice.id, request } })}
            onSaved={(_saved, intent) => {
              void refresh()
              if (intent === "send") setSending(true)
            }}
            onCreateCustomer={(request) => createCustomer({ data: request })}
            branding={
              branding
                ? { default_template: branding.default_template, brand_color: branding.brand_color }
                : undefined
            }
            onSaveDefaultTemplate={async (template) => {
              const result = await saveBranding({ data: { default_template: template } })
              if (result.ok) void refresh()
              return result
            }}
            onSaveSeller={async (request) => {
              const result = await saveSeller({ data: request })
              if (result.ok) void refresh()
              return result
            }}
          />
          {invoice.number === null && (
            <div className="mx-auto w-full max-w-7xl px-6 pb-8">
              <Button variant="ghost" onClick={() => setDeleting(true)}>
                Delete draft
              </Button>
            </div>
          )}
        </>
      ) : (
        document && (
          <InvoiceDetailView
            invoice={invoice}
            document={document}
            today={today}
            onOpenPayment={setPayment}
            onVoid={() => voidInvoice({ data: { id: invoice.id } })}
            onDeletePayment={(paymentId) => deletePayment({ data: { paymentId } })}
            onDownloadPdf={() => pdfUrl({ data: { id: invoice.id } })}
            onChanged={() => {
              setNotice(null) // a stale "receipt could not be issued" must not outlive the next action
              void refresh()
            }}
            receipts={receipts}
            notice={notice}
            onIssueReceipt={(paymentId) => issueReceipt({ data: { paymentId } })}
            onLoadReceiptDocument={(id) => receiptDocument({ data: { id } })}
            onReceiptPdf={(id) => receiptPdf({ data: { id } })}
            onEmailReceipt={(id) => sendReceipt({ data: { id } })}
          />
        )
      )}

      {sending && (
        <SendDialog
          invoice={invoice}
          defaultTo={invoice.send_to_email ?? invoice.customer?.email ?? ""}
          sellerName={seller.business_name ?? ""}
          open
          onOpenChange={(next) => !next && setSending(false)}
          onRequest={(toEmail) => send({ data: { id: invoice.id, toEmail } })}
          onUndo={(sendToken) => undoSend({ data: { id: invoice.id, sendToken } })}
          onProcess={(sendToken) => processSend({ data: { id: invoice.id, sendToken } })}
          onSettled={() => void refresh()}
        />
      )}
      {payment && (
        <PaymentDialog
          invoice={invoice}
          today={today}
          mode={payment}
          open
          onOpenChange={(next) => !next && setPayment(null)}
          onSubmit={(request) => recordPayment({ data: { invoiceId: invoice.id, request } })}
          onIssueReceipt={async (updated) => {
            // the payment just recorded is the one the page did not know before
            const known = new Set(invoice.payments.map((entry) => entry.id))
            const created = updated.payments.find((entry) => !known.has(entry.id))
            if (!created)
              return { ok: false, error: { code: "NOT_FOUND", message: "Payment not found." } }
            return issueReceipt({ data: { paymentId: created.id } })
          }}
          onDone={(_updated, outcome) => {
            setPayment(null)
            setNotice(
              outcome.receiptFailed
                ? "The payment was recorded, but its receipt could not be issued. Use Issue receipt on the payment."
                : null,
            )
            void refresh()
          }}
        />
      )}
      {voiding && (
        <Dialog open onOpenChange={(next) => !next && setVoiding(false)}>
          <DialogContent role="alertdialog">
            <DialogHeader>
              <DialogTitle>Void {invoice.number}?</DialogTitle>
              <DialogDescription>
                The invoice stays on record marked void. The number stays used: invoice numbers are
                never reused or deleted.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setVoiding(false)}>
                Keep invoice
              </Button>
              <Button
                variant="destructive"
                onClick={async () => {
                  const result = await voidInvoice({ data: { id: invoice.id } })
                  if (result.ok) {
                    setVoiding(false)
                    void refresh()
                  }
                }}
              >
                Void invoice
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {deleting && (
        <ConfirmDeleteDraft
          invoice={invoice}
          onCancel={() => setDeleting(false)}
          onDelete={() => deleteDraft({ data: { id: invoice.id } })}
          onDone={() => router.navigate({ to: "/invoices" })}
        />
      )}
    </>
  )
}
